import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Radio, Checkbox, Select } from 'antd';
import type { HamlibControlRequestMessage, HamlibControlResponseMessage, HamlibControlData } from './amplifier';

export interface ProtocolModeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 由 MQTT 获取的 set_hamlib 消息，由此传入弹窗展示 */
  hamlibControlRequestMessage?: HamlibControlRequestMessage | null;
  /** 点击 command 按钮时回调：0=连接, 1=启动PTT1测试, 2=启动PTT2测试，可由父组件下发 MQTT */
  onCommandClick?: (command: number) => void;
  /** 失焦时把修改后的 data 封装成 HamlibControlResponseMessage 通过此回调发送到 MQTT */
  onHamlibSave?: (message: HamlibControlResponseMessage) => void;
}

/** 协议模式配置弹窗：端口1/端口2 等参数来自 HamlibControlRequestMessage.data */
export const ProtocolModeModal: React.FC<ProtocolModeModalProps> = ({
  isOpen,
  onClose,
  hamlibControlRequestMessage,
  onCommandClick,
  onHamlibSave,
}) => {
  const data = hamlibControlRequestMessage?.data;
  const avaliableRigList = (data && 'avaliable_rig' in data ? (data as { avaliable_rig?: string[] }).avaliable_rig : undefined) ?? data?.avaliable_rig ?? [];
  const avaliableComList = data?.avaliable_com ?? [];
  const avaliableBaudList = Array.isArray(data?.avaliable_boardrate)
    ? (data?.avaliable_boardrate ?? []).map((b) => String(b))
    : [];

  const [rig, setRig] = useState<string>(data?.rig ?? '');
  useEffect(() => {
    if (data?.rig != null) setRig(data.rig);
  }, [data?.rig]);

  const [serial1Port, setSerial1Port] = useState<string>(data?.serial1_port ?? '');
  const [serial2Port, setSerial2Port] = useState<string>(data?.serial2_port ?? '');
  useEffect(() => {
    if (data?.serial1_port != null) setSerial1Port(data.serial1_port);
  }, [data?.serial1_port]);
  useEffect(() => {
    if (data?.serial2_port != null) setSerial2Port(data.serial2_port);
  }, [data?.serial2_port]);

  const [serial1Baud, setSerial1Baud] = useState<string>(data?.serial1_boardrate != null ? String(data.serial1_boardrate) : '');
  const [serial2Baud, setSerial2Baud] = useState<string>(data?.serial2_boardrate != null ? String(data.serial2_boardrate) : '');
  useEffect(() => {
    if (data?.serial1_boardrate != null) setSerial1Baud(String(data.serial1_boardrate));
  }, [data?.serial1_boardrate]);
  useEffect(() => {
    if (data?.serial2_boardrate != null) setSerial2Baud(String(data.serial2_boardrate));
  }, [data?.serial2_boardrate]);

  const [serverIpLocal, setServerIpLocal] = useState<string>(data?.server_ip ?? '');
  useEffect(() => {
    setServerIpLocal(hamlibControlRequestMessage?.data?.server_ip ?? '');
  }, [hamlibControlRequestMessage?.data?.server_ip]);

  const [serverPortLocal, setServerPortLocal] = useState<string>(data?.server_port != null ? String(data.server_port) : '');
  useEffect(() => {
    setServerPortLocal(hamlibControlRequestMessage?.data?.server_port != null ? String(hamlibControlRequestMessage.data.server_port) : '');
  }, [hamlibControlRequestMessage?.data?.server_port]);

  type Ptt1Mode = 'off' | 'rts' | 'dtr' | 'cat';
  const getPtt1FromData = (): Ptt1Mode => {
    const d = hamlibControlRequestMessage?.data;
    if (!d) return 'off';
    if (d.ptt1_via_off === 1) return 'off';
    if (d.ptt1_via_rts === 1) return 'rts';
    if (d.ptt1_via_dtr === 1) return 'dtr';
    if (d.ptt1_via_cat === 1) return 'cat';
    return 'off';
  };
  const [port1PttLocal, setPort1PttLocal] = useState<Ptt1Mode>(getPtt1FromData());
  useEffect(() => {
    setPort1PttLocal(getPtt1FromData());
  }, [
    hamlibControlRequestMessage?.data?.ptt1_via_off,
    hamlibControlRequestMessage?.data?.ptt1_via_rts,
    hamlibControlRequestMessage?.data?.ptt1_via_dtr,
    hamlibControlRequestMessage?.data?.ptt1_via_cat,
  ]);

  const d = data;
  const dataAny = d as Record<string, unknown> | undefined;
  const [readRtsOn, setReadRtsOn] = useState<boolean>(dataAny?.read_rts_on === 1);
  const [readDtrOn, setReadDtrOn] = useState<boolean>(dataAny?.read_dtr_on === 1);
  useEffect(() => {
    setReadRtsOn(dataAny?.read_rts_on === 1);
    setReadDtrOn(dataAny?.read_dtr_on === 1);
  }, [dataAny?.read_rts_on, dataAny?.read_dtr_on]);

  type PttMode = 'ptt' | 'mic' | 'data';
  const getPttFromData = (): PttMode => {
    if (!d) return 'ptt';
    if (d.ptt === 1) return 'ptt';
    if (d.ptt_mic === 1) return 'mic';
    if (d.ptt_data === 1) return 'data';
    return 'ptt';
  };
  const [pttLocal, setPttLocal] = useState<PttMode>(getPttFromData());
  useEffect(() => {
    setPttLocal(getPttFromData());
  }, [d?.ptt, d?.ptt_mic, d?.ptt_data]);

  const [pollLocal, setPollLocal] = useState<string>(d?.poll != null ? String(d.poll) : '');
  useEffect(() => {
    setPollLocal(d?.poll != null ? String(d.poll) : '');
  }, [d?.poll]);

  const [tunDeviceLocal, setTunDeviceLocal] = useState<number>(d?.tun_device === 1 ? 1 : 0);
  useEffect(() => {
    setTunDeviceLocal(d?.tun_device === 1 ? 1 : 0);
  }, [d?.tun_device]);

  const [rigModeLocal, setRigModeLocal] = useState<number>(d?.rig_mode ?? 0);
  useEffect(() => {
    setRigModeLocal(d?.rig_mode ?? 0);
  }, [d?.rig_mode]);

  const [useSelectedLocal, setUseSelectedLocal] = useState<boolean>(d?.use_selected ?? false);
  useEffect(() => {
    setUseSelectedLocal(d?.use_selected ?? false);
  }, [d?.use_selected]);

  const [freqInputLocal, setFreqInputLocal] = useState<string>(d?.freq_input != null ? String(d.freq_input) : '');
  useEffect(() => {
    setFreqInputLocal(d?.freq_input != null ? String(d.freq_input) : '');
  }, [d?.freq_input]);

  const [freqInputTypeLocal, setFreqInputTypeLocal] = useState<number>(d?.freq_input_type ?? 0);
  useEffect(() => {
    setFreqInputTypeLocal(d?.freq_input_type ?? 0);
  }, [d?.freq_input_type]);

  const [ptt2ViaLocal, setPtt2ViaLocal] = useState<number>(d?.ptt2_via ?? 0);
  useEffect(() => {
    setPtt2ViaLocal(d?.ptt2_via ?? 0);
  }, [d?.ptt2_via]);

  const [txWatchdogLocal, setTxWatchdogLocal] = useState<number>(d?.tx_watchdog ?? 0);
  const [txTimeLocal, setTxTimeLocal] = useState<string>(d?.tx_time != null ? String(d.tx_time) : '');
  const [txPeriodsLocal, setTxPeriodsLocal] = useState<string>(d?.tx_periods != null ? String(d.tx_periods) : '');
  useEffect(() => {
    setTxWatchdogLocal(d?.tx_watchdog ?? 0);
    setTxTimeLocal(d?.tx_time != null ? String(d.tx_time) : '');
    setTxPeriodsLocal(d?.tx_periods != null ? String(d.tx_periods) : '');
  }, [d?.tx_watchdog, d?.tx_time, d?.tx_periods]);

  const buildHamlibResponse = (overrides?: {
    serial1_port?: string;
    serial1_boardrate?: number;
    serial2_port?: string;
    serial2_boardrate?: number;
    ptt1_via_off?: number;
    ptt1_via_rts?: number;
    ptt1_via_dtr?: number;
    ptt1_via_cat?: number;
    rig?: string;
    ptt?: number;
    ptt_mic?: number;
    ptt_data?: number;
    poll?: number;
    tun_device?: number;
    rig_mode?: number;
    use_selected?: boolean;
    freq_input?: number;
    freq_input_type?: number;
    ptt2_via?: number;
    tx_watchdog?: number;
    tx_time?: number;
    tx_periods?: number;
    read_rts_on?: number;
    read_dtr_on?: number;
  }) => {
    if (!hamlibControlRequestMessage?.data || !onHamlibSave) return;
    const d = hamlibControlRequestMessage.data;
    const portNum = serverPortLocal.trim() === '' ? d.server_port : Number(serverPortLocal);
    const serial1BaudNum = overrides?.serial1_boardrate ?? (serial1Baud.trim() !== '' ? Number(serial1Baud) : d.serial1_boardrate);
    const serial2BaudNum = serial2Baud.trim() !== '' ? Number(serial2Baud) : d.serial2_boardrate;
    const ptt1Off = overrides?.ptt1_via_off ?? (port1PttLocal === 'off' ? 1 : d.ptt1_via_off === 2 ? 2 : 0);
    const ptt1Rts = overrides?.ptt1_via_rts ?? (port1PttLocal === 'rts' ? 1 : d.ptt1_via_rts === 2 ? 2 : 0);
    const ptt1Dtr = overrides?.ptt1_via_dtr ?? (port1PttLocal === 'dtr' ? 1 : d.ptt1_via_dtr === 2 ? 2 : 0);
    const ptt1Cat = overrides?.ptt1_via_cat ?? (port1PttLocal === 'cat' ? 1 : d.ptt1_via_cat === 2 ? 2 : 0);
    const pollNum = overrides?.poll ?? (pollLocal.trim() !== '' ? Number(pollLocal) : d.poll);
    const freqInputNum = overrides?.freq_input ?? (freqInputLocal.trim() !== '' ? Number(freqInputLocal) : d.freq_input);
    const txTimeNum = overrides?.tx_time ?? (txTimeLocal.trim() !== '' ? Number(txTimeLocal) : d.tx_time);
    const txPeriodsNum = overrides?.tx_periods ?? (txPeriodsLocal.trim() !== '' ? Number(txPeriodsLocal) : d.tx_periods);
    const responseMessage: HamlibControlResponseMessage = {
      ...hamlibControlRequestMessage,
      type: 'response',
      cmd: 'set_hamlib',
      timestamp: Date.now(),
      data: {
        ...d,
        server_ip: serverIpLocal,
        server_port: Number.isNaN(portNum) ? d.server_port : portNum,
        serial1_port: overrides?.serial1_port ?? serial1Port,
        serial1_boardrate: Number.isNaN(serial1BaudNum) ? d.serial1_boardrate : serial1BaudNum,
        serial2_port: overrides?.serial2_port ?? serial2Port,
        serial2_boardrate: Number.isNaN(serial2BaudNum) ? d.serial2_boardrate : serial2BaudNum,
        ptt1_via_off: ptt1Off,
        ptt1_via_rts: ptt1Rts,
        ptt1_via_dtr: ptt1Dtr,
        ptt1_via_cat: ptt1Cat,
        rig: overrides?.rig ?? rig,
        ptt: overrides?.ptt ?? (pttLocal === 'ptt' ? 1 : d.ptt === 2 ? 2 : 0),
        ptt_mic: overrides?.ptt_mic ?? (pttLocal === 'mic' ? 1 : d.ptt_mic === 2 ? 2 : 0),
        ptt_data: overrides?.ptt_data ?? (pttLocal === 'data' ? 1 : d.ptt_data === 2 ? 2 : 0),
        poll: overrides?.poll ?? (typeof pollNum === 'number' && !Number.isNaN(pollNum) ? pollNum : d.poll),
        tun_device: overrides?.tun_device ?? tunDeviceLocal,
        rig_mode: overrides?.rig_mode ?? rigModeLocal,
        use_selected: overrides?.use_selected ?? useSelectedLocal,
        freq_input: overrides?.freq_input ?? (Number.isNaN(freqInputNum) ? d.freq_input : freqInputNum),
        freq_input_type: overrides?.freq_input_type ?? freqInputTypeLocal,
        ptt2_via: overrides?.ptt2_via ?? ptt2ViaLocal,
        tx_watchdog: overrides?.tx_watchdog ?? txWatchdogLocal,
        tx_time: overrides?.tx_time ?? (Number.isNaN(txTimeNum) ? d.tx_time : txTimeNum),
        tx_periods: overrides?.tx_periods ?? (Number.isNaN(txPeriodsNum) ? d.tx_periods : txPeriodsNum),
        read_rts_on: overrides?.read_rts_on ?? (readRtsOn ? 1 : 0),
        read_dtr_on: overrides?.read_dtr_on ?? (readDtrOn ? 1 : 0),
      } as HamlibControlData,
    };
    onHamlibSave(responseMessage);
    syncFromHamlibRequest();
  };

  /** 所有展示用 state 仅从 HamlibControlRequestMessage.data 同步，发送后调用以保持“只显示服务器下发的数据” */
  const syncFromHamlibRequest = () => {
    const d = hamlibControlRequestMessage?.data;
    if (!d) return;
    setServerIpLocal(d.server_ip ?? '');
    setServerPortLocal(d.server_port != null ? String(d.server_port) : '');
    setRig(d.rig ?? '');
    setSerial1Port(d.serial1_port ?? '');
    setSerial2Port(d.serial2_port ?? '');
    setSerial1Baud(d.serial1_boardrate != null ? String(d.serial1_boardrate) : '');
    setSerial2Baud(d.serial2_boardrate != null ? String(d.serial2_boardrate) : '');
    setPort1PttLocal(getPtt1FromData());
    setReadRtsOn((d as unknown as Record<string, unknown>).read_rts_on === 1);
    setReadDtrOn((d as unknown as Record<string, unknown>).read_dtr_on === 1);
    setPttLocal(getPttFromData());
    setPollLocal(d.poll != null ? String(d.poll) : '');
    setTunDeviceLocal(d.tun_device === 1 ? 1 : 0);
    setRigModeLocal(d.rig_mode ?? 0);
    setUseSelectedLocal(d.use_selected ?? false);
    setFreqInputLocal(d.freq_input != null ? String(d.freq_input) : '');
    setFreqInputTypeLocal(d.freq_input_type ?? 0);
    setPtt2ViaLocal(d.ptt2_via ?? 0);
    setTxWatchdogLocal(d.tx_watchdog ?? 0);
    setTxTimeLocal(d.tx_time != null ? String(d.tx_time) : '');
    setTxPeriodsLocal(d.tx_periods != null ? String(d.tx_periods) : '');
  };

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, color: 'white', minWidth: 900 }}>
      {/* 端口1 */}
      <div style={{ backgroundColor: '#1a1a1a', padding: 16, borderRadius: 8 }}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600 }}>端口1</span>
          <span style={{ color: 'red' }}>端口1: 断开</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
          <span style={{ minWidth: 60 }}>服务器:</span>
          <Input
            value={serverIpLocal}
            onChange={(e) => setServerIpLocal(e.target.value)}
            onBlur={() => buildHamlibResponse()}
            style={{ width: 140, backgroundColor: '#333', borderColor: '#555', color: 'white' }}
          />
          <span style={{ minWidth: 36 }}>端口:</span>
          <Input
            value={serverPortLocal}
            onChange={(e) => setServerPortLocal(e.target.value)}
            onBlur={() => buildHamlibResponse()}
            style={{ width: 80, backgroundColor: '#333', borderColor: '#555', color: 'white' }}
          />
          <Button style={{ backgroundColor: '#333', borderColor: '#555', color: 'white' }} onClick={() => onCommandClick?.(0)}>连接</Button>
          <span style={{ marginLeft: 8, minWidth: 36 }}>端口:</span>
          <Select
            value={serial1Port || undefined}
            onChange={(v) => {
              setSerial1Port(v ?? '');
              buildHamlibResponse({ serial1_port: v ?? '' });
            }}
            options={avaliableComList.map((item) => ({ label: item, value: item }))}
            placeholder="请选择串口"
            style={{ width: 100 }}
            allowClear
          />
          <span>波特率:</span>
          <Select
            value={serial1Baud || undefined}
            onChange={(v) => {
              setSerial1Baud(v ?? '');
              const num = v ? Number(v) : undefined;
              buildHamlibResponse({ serial1_boardrate: num ?? hamlibControlRequestMessage?.data?.serial1_boardrate });
            }}
            options={avaliableBaudList.map((item) => ({ label: item, value: item }))}
            placeholder="请选择波特率"
            style={{ width: 80 }}
            allowClear
          />
        </div>
        <div style={{ marginBottom: 8, display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ marginRight: 0 }}>PTT 方式:</span>
          <Radio.Group
            value={port1PttLocal}
            onChange={(e) => {
              const v = e.target.value as Ptt1Mode;
              setPort1PttLocal(v);
              const d = hamlibControlRequestMessage?.data;
              if (!d || !onHamlibSave) return;
              buildHamlibResponse({
                ptt1_via_off: v === 'off' ? 1 : d.ptt1_via_off === 2 ? 2 : 0,
                ptt1_via_rts: v === 'rts' ? 1 : d.ptt1_via_rts === 2 ? 2 : 0,
                ptt1_via_dtr: v === 'dtr' ? 1 : d.ptt1_via_dtr === 2 ? 2 : 0,
                ptt1_via_cat: v === 'cat' ? 1 : d.ptt1_via_cat === 2 ? 2 : 0,
              });
            }}
          >
            <Radio value="off" disabled={hamlibControlRequestMessage?.data?.ptt1_via_off === 2} style={{ color: 'white' }}>PTT 关闭</Radio>
            <Radio value="rts" disabled={hamlibControlRequestMessage?.data?.ptt1_via_rts === 2} style={{ color: 'white' }}>PTT 通过 RTS</Radio>
            <Radio value="dtr" disabled={hamlibControlRequestMessage?.data?.ptt1_via_dtr === 2} style={{ color: 'white' }}>PTT 通过 DTR</Radio>
            <Radio value="cat" disabled={hamlibControlRequestMessage?.data?.ptt1_via_cat === 2} style={{ color: 'white' }}>PTT 通过 CAT 指令</Radio>
          </Radio.Group>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Checkbox
              checked={readRtsOn}
              onChange={(e) => {
                setReadRtsOn(e.target.checked);
                buildHamlibResponse({ read_rts_on: e.target.checked ? 1 : 0 });
              }}
              style={{ color: 'white' }}
            >
              启用读取 RTS ON
            </Checkbox>
            <Checkbox
              checked={readDtrOn}
              onChange={(e) => {
                setReadDtrOn(e.target.checked);
                buildHamlibResponse({ read_dtr_on: e.target.checked ? 1 : 0 });
              }}
              style={{ color: 'white' }}
            >
              启用读取 DTR ON
            </Checkbox>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <span>RIG:</span>
          <Select
            value={rig || undefined}
            onChange={(v) => {
              setRig(v ?? '');
              buildHamlibResponse({ rig: v ?? '' });
            }}
            options={avaliableRigList.map((item) => ({ label: item, value: item }))}
            placeholder="请选择 RIG"
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            allowClear
          />
          <Radio.Group
            value={pttLocal}
            onChange={(e) => {
              const v = e.target.value as PttMode;
              setPttLocal(v);
              const dx = hamlibControlRequestMessage?.data;
              if (!dx || !onHamlibSave) return;
              buildHamlibResponse({
                ptt: v === 'ptt' ? 1 : dx.ptt === 2 ? 2 : 0,
                ptt_mic: v === 'mic' ? 1 : dx.ptt_mic === 2 ? 2 : 0,
                ptt_data: v === 'data' ? 1 : dx.ptt_data === 2 ? 2 : 0,
              });
            }}
          >
            <Radio value="ptt" disabled={hamlibControlRequestMessage?.data?.ptt === 2} style={{ color: 'white' }}>PTT</Radio>
            <Radio value="mic" disabled={hamlibControlRequestMessage?.data?.ptt_mic === 2} style={{ color: 'white' }}>PTT MIC</Radio>
            <Radio value="data" disabled={hamlibControlRequestMessage?.data?.ptt_data === 2} style={{ color: 'white' }}>PTT DATA</Radio>
          </Radio.Group>
          <span style={{ marginLeft: 8 }}>
            Poll, int{' '}
            <Input
              value={pollLocal}
              onChange={(e) => setPollLocal(e.target.value)}
              onBlur={() => buildHamlibResponse()}
              style={{ width: 60, display: 'inline-block', margin: '0 4px', verticalAlign: 'middle', backgroundColor: '#333', borderColor: '#555', color: 'white' }}
            />
            {' '}ms
          </span>

          <Radio.Group
            value={tunDeviceLocal}
            onChange={(e) => {
              const v = e.target.value as number;
              setTunDeviceLocal(v);
              buildHamlibResponse({ tun_device: v });
            }}
            style={{ color: 'white' }}
          >
            <Radio value={0} style={{ color: 'white' }}>仅通过按下按钮F 调谐无线电设备 频率/模式</Radio>
            <Radio value={1} style={{ color: 'white' }}>调谐无线电设备 频率/模式</Radio>
          </Radio.Group>

          <span>RIG Mode:</span>
          <Select
            value={rigModeLocal}
            onChange={(v) => {
              setRigModeLocal(v ?? 0);
              buildHamlibResponse({ rig_mode: v ?? 0 });
            }}
            options={[
              { label: 'None', value: 0 },
              { label: 'USB', value: 1 },
              { label: 'DIGU', value: 2 },
            ]}
            style={{ width: 100 }}
          />
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Button style={{ backgroundColor: '#333', borderColor: '#555', color: 'white' }} onClick={() => onCommandClick?.(1)}>启动 PTT 测试 无选择端口</Button>
          </div>
          
        </div>
        
      </div>

      {/* 中部：TX 音频频率 + 关闭按钮 */}
      <div style={{ backgroundColor: '#1a1a1a', padding: 16, borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 12, color: '#aaa' }}>
          选定的恒定TX音频频率 FT Q65 (无线电设备频率必须是通过CAT可读&可写的)
        </div>
        <Radio.Group
          value={useSelectedLocal}
          onChange={(e) => {
            const v = e.target.value as boolean;
            setUseSelectedLocal(v);
            buildHamlibResponse({ use_selected: v });
          }}
        >
          <Radio value={true} style={{ color: 'white' }}>使用选定的恒定TX音频频率 FT Q65     选择音频频率：def=1500 1500HZ</Radio>
        </Radio.Group>

        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <Button type="primary" onClick={onClose} style={{ backgroundColor: 'green', borderColor: 'green' }}>
            close
          </Button>
        </div>
      </div>

      {/* 端口2 */}
      <div style={{ backgroundColor: '#1a1a1a', padding: 16, borderRadius: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Radio.Group
            value={freqInputTypeLocal}
            onChange={(e) => {
              const v = e.target.value as number;
              setFreqInputTypeLocal(v);
              buildHamlibResponse({ freq_input_type: v });
            }}
          >
            <Radio value={0} style={{ color: 'white' }}>关闭</Radio>
            <Radio value={1} style={{ color: 'white' }}>总和</Radio>
            <Radio value={2} style={{ color: 'white' }}>减去</Radio>
          </Radio.Group>
          <span>频率输入 Hz:</span>
          <Input
            value={freqInputLocal}
            onChange={(e) => setFreqInputLocal(e.target.value)}
            onBlur={() => buildHamlibResponse()}
            style={{ width: 100, backgroundColor: '#333', borderColor: '#555', color: 'white' }}
          />
          <span>RIG: 70.000.000 Hz</span>
        </div>
        <div style={{ marginBottom: 12, fontWeight: 600 }}>端口2</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ marginRight: 8 }}>PTT 方式:</span>
          <Radio.Group
            value={ptt2ViaLocal}
            onChange={(e) => {
              const v = e.target.value as number;
              setPtt2ViaLocal(v);
              buildHamlibResponse({ ptt2_via: v });
            }}
          >
            <Radio value={0} style={{ color: 'white' }}>PTT2关闭</Radio>
            <Radio value={1} style={{ color: 'white' }}>PTT2通过DTR</Radio>
          </Radio.Group>
          <span>端口:</span>
          <Select
            value={serial2Port || undefined}
            onChange={(v) => {
              setSerial2Port(v ?? '');
              buildHamlibResponse({ serial2_port: v ?? '' });
            }}
            options={avaliableComList.map((item) => ({ label: item, value: item }))}
            placeholder="请选择串口"
            style={{ width: 100 }}
            allowClear
          />
          <span>波特率:</span>
          <Select
            value={serial2Baud || undefined}
            onChange={(v) => {
              setSerial2Baud(v ?? '');
              const num = v ? Number(v) : undefined;
              buildHamlibResponse({ serial2_boardrate: num ?? hamlibControlRequestMessage?.data?.serial2_boardrate });
            }}
            options={avaliableBaudList.map((item) => ({ label: item, value: item }))}
            placeholder="请选择波特率"
            style={{ width: 80 }}
            allowClear
          />
        </div>
        <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}>
          <Button style={{ backgroundColor: '#333', borderColor: '#555', color: 'white' }} onClick={() => onCommandClick?.(2)}>启动 PTT 测试 无选择端口</Button>
        </div>
        <div>
          <span style={{ marginRight: 8 }}>Tx 计时器:</span>
          <Radio.Group
            value={txWatchdogLocal}
            onChange={(e) => {
              const v = e.target.value as number;
              setTxWatchdogLocal(v);
              buildHamlibResponse({ tx_watchdog: v });
            }}
          >
            <Radio value={0} style={{ color: 'white' }}>关闭</Radio>
            <Radio value={1} style={{ color: 'white' }}>及时 def={hamlibControlRequestMessage?.data?.tx_time ?? 20}</Radio>
            <Radio value={2} style={{ color: 'white' }}>在 TX 周期数中 def={hamlibControlRequestMessage?.data?.tx_periods ?? 10}</Radio>
          </Radio.Group>
          {txWatchdogLocal === 1 && (
            <span style={{ marginLeft: 4 }}>
              <Input
                value={txTimeLocal}
                onChange={(e) => setTxTimeLocal(e.target.value)}
                onBlur={() => buildHamlibResponse()}
                style={{ width: 48, backgroundColor: '#333', borderColor: '#555', color: 'white', marginLeft: 4 }}
              />
            </span>
          )}
          {txWatchdogLocal === 2 && (
            <span style={{ marginLeft: 4 }}>
              <Input
                value={txPeriodsLocal}
                onChange={(e) => setTxPeriodsLocal(e.target.value)}
                onBlur={() => buildHamlibResponse()}
                style={{ width: 48, backgroundColor: '#333', borderColor: '#555', color: 'white', marginLeft: 4 }}
              />
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      title={null}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={960}
      style={{ marginTop: '-10vh' }}
      styles={{ body: { backgroundColor: '#111', maxHeight: '90vh', minHeight: '70vh', overflow: 'auto' } }}
      closable={true}
    >
      {content}
    </Modal>
  );
};

export default ProtocolModeModal;
